import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Icon } from '@iconify/react'
import { LOGIN_TOKEN } from 'domain/types/authentication/authentication'
import { LoginByAccessToken, LoginByToken } from 'base/src/store/authentication/action/authenticationAction'
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil'
import { RootStateBase } from '../../store/baseStore'
import { RouteParamters } from '../../utils/routeUtil'

type AuthError =
  | { type: 'malformed' }
  | { type: 'expired' }
  | { type: 'missing-claims'; claims: string[] }
  | { type: 'auth-failed' }

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

const connector = connectUtil(
  (state: RootStateBase) => ({
    authState: state.AuthenticationReducer.authState,
    loading: state.BaseReducer.Loading,
  }),
  { LoginByToken, LoginByAccessToken }
)

interface Props extends PropsFromRedux<typeof connector> {
  requiredClaims?: string[]
  validatorUrl?: string
}

function ErrorCard({ error }: { error: AuthError }) {
  if (error.type === 'expired') {
    return (
      <>
        <Icon icon="ph:clock-countdown" height={64} color="#ef4444" />
        <Title>Token expirado</Title>
        <Message>
          O link de acesso utilizado já expirou. Solicite um novo link ao administrador do sistema.
        </Message>
      </>
    )
  }

  if (error.type === 'missing-claims') {
    return (
      <>
        <Icon icon="ph:warning-circle" height={64} color="#f59e0b" />
        <Title>Token incompleto</Title>
        <Message>
          O token de acesso está faltando as seguintes informações obrigatórias:{' '}
          <strong>{error.claims.join(', ')}</strong>. Entre em contato com o administrador do
          sistema.
        </Message>
      </>
    )
  }

  return (
    <>
      <Icon icon="ph:lock-key-open" height={64} color="#ef4444" />
      <Title>Token inválido</Title>
      <Message>
        O link de acesso utilizado não é válido ou já expirou. Entre em contato com o administrador
        do sistema para obter um novo acesso.
      </Message>
    </>
  )
}

function AccessTokenAuth(props: Props) {
  const [searchParams] = useSearchParams()
  const [attemptStarted, setAttemptStarted] = useState(false)
  const [attemptCompleted, setAttemptCompleted] = useState(false)
  const hasSeenLoading = useRef(false)

  const accessToken = searchParams.get(RouteParamters.access_token)?.replace(/\s/g, '') || null
  const isLoading = props.loading.some((e) => e.Name === LOGIN_TOKEN)

  const localError = useMemo<AuthError | null>(() => {
    if (!accessToken) return { type: 'malformed' }
    const payload = decodeJwtPayload(accessToken)
    if (!payload) return { type: 'malformed' }
    const now = Date.now() / 1000
    if (payload.exp && (payload.exp as number) < now) return { type: 'expired' }
    const required = props.requiredClaims ?? []
    const missing = required.filter((claim) => !payload[claim])
    if (missing.length > 0) return { type: 'missing-claims', claims: missing }
    return null
  }, [accessToken])

  useEffect(() => {
    if (localError) return
    if (!attemptStarted) {
      setAttemptStarted(true)
      if (props.validatorUrl) {
        props.LoginByAccessToken(accessToken!, props.validatorUrl)
      } else {
        props.LoginByToken(accessToken, false)
      }
    }
  }, [attemptStarted, localError])

  useEffect(() => {
    if (isLoading) hasSeenLoading.current = true
    if (attemptStarted && !isLoading && !attemptCompleted && hasSeenLoading.current) {
      setAttemptCompleted(true)
    }
  }, [attemptStarted, isLoading, attemptCompleted])

  if (props.authState) return null

  if (localError) {
    return (
      <FullPage>
        <Card>
          <ErrorCard error={localError} />
        </Card>
      </FullPage>
    )
  }

  if (!attemptCompleted || isLoading) {
    return <FullPage><p>Autenticando...</p></FullPage>
  }

  return (
    <FullPage>
      <Card>
        <ErrorCard error={{ type: 'auth-failed' }} />
      </Card>
    </FullPage>
  )
}

export default connector(AccessTokenAuth)

const FullPage = styled.div`
  height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 48px;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  max-width: 480px;
  text-align: center;
`

const Title = styled.h2`
  color: #ef4444;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
`

const Message = styled.p`
  color: #374151;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
`
